import * as React from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'

import {Hero, ContactForm, CTAButton} from '@components/ai-readiness'

const MainLayout = dynamic(() => import('@components/MainLayout'))
const Meta = dynamic(() => import('@components/Meta'))

export default function AIReadiness() {
  const whyReadinessList = [
    {
      icon: '/images/ai/readiness/01-strategy.svg',
      title1: 'กลยุทธ์',
      title2: '(Strategy)',
      body: 'เป็นการประเมินความพร้อมเชิงนโยบาย ซึ่งรวมถึง ความชัดเจนของกลยุทธ์ในการนำเทคโนโลยี AI เข้ามาใช้ปรับเปลี่ยนรูปแบบการทำงานในองค์กรและต่อยอดสู่การใช้งานเชิงธุรกิจ',
    },
    {
      icon: '/images/ai/readiness/02-adaptation.svg',
      title1: 'ความสามารถในการปรับตัว',
      title2: '(Adaptability)',
      body: 'เป็นการประเมินความคล่องตัวและความพร้อมในการเปลี่ยนแปลงขององค์กร ทั้งในเชิงโครงสร้างและเชิงนโยบาย',
    },
    {
      icon: '/images/ai/readiness/03-infrastructure.svg',
      title1: 'ความพร้อมของโครงสร้างพื้นฐานด้านข้อมูล',
      title2: '(Data Infrastructure)',
      body: 'เป็นการประเมินเพื่อทำความเข้าใจเกี่ยวกับโครงสร้างพื้นฐานสำหรับการจัดเก็บข้อมูลภายในองค์กรอย่างเป็นระบบ',
    },
    {
      icon: '/images/ai/readiness/04-techcap.svg',
      title1: 'ความสามารถด้านเทคโนโลยี',
      title2: '(Tech Capabilities)',
      body: 'เป็นการประเมินความสามารถเชิงเทคนิคขององค์กร ครอบคลุมตั้งแต่การตรวจสอบศักยภาพและความพร้อมของทีมงาน ไปจนถึงการวิเคราะห์ประสิทธิภาพของการใช้เทคโนโลยีในด้านต่าง ๆ',
    },
  ]
  return (
    <React.Fragment>
      <Meta title='AI Readiness | Datawow – Data Science Consultant Thailand | Data Wow in Bangkok' />

      <div className='AIReadiness'>
        <Hero />

        <div className='AIReadiness__intro'>
          <div className='container'>
            <div className='AIReadiness__intro__content'>
              <svg
                width='82'
                height='70'
                viewBox='0 0 82 70'
                fill='currentColor'
                className='AIReadiness__intro__quote AIReadiness__intro__quote--left'
              >
                <path d='M37.4081 70H0V41.2616C0 30.6802 2.20802 22.3296 6.62402 16.2097C11.04 10.0898 18.6081 4.68655 29.3284 0L37.4081 15.2698C30.8179 18.3541 26.2609 21.4272 23.7369 24.489C21.2092 27.566 19.7326 31.3679 19.5222 35.3405H37.4081V70ZM81.0112 70H43.6032V41.2616C43.6032 30.6802 45.8093 22.3296 50.2216 16.2097C54.6338 10.0898 62.2019 4.68655 72.9259 0L81.0112 15.2698C74.4174 18.3541 69.8603 21.4272 67.3401 24.489C64.8062 27.5624 63.3287 31.3664 63.1254 35.3405H81.0112V70Z' />
              </svg>

              <svg
                width='82'
                height='70'
                viewBox='0 0 82 70'
                fill='currentColor'
                className='AIReadiness__intro__quote AIReadiness__intro__quote--right'
              >
                <path d='M44.5919 70H82V41.2616C82 30.6802 79.792 22.3296 75.376 16.2097C70.96 10.0898 63.3919 4.68655 52.6716 0L44.5919 15.2698C51.1821 18.3541 55.7391 21.4272 58.2631 24.489C60.7908 27.566 62.2674 31.3679 62.4778 35.3405H44.5919V70ZM0.988754 70H38.3968V41.2616C38.3968 30.6802 36.1907 22.3296 31.7784 16.2097C27.3662 10.0898 19.7981 4.68655 9.07409 0L0.988754 15.2698C7.58264 18.3541 12.1397 21.4272 14.6599 24.489C17.1938 27.5624 18.6713 31.3664 18.8746 35.3405H0.988754V70Z' />
              </svg>

              <h2>
                อย่าเพิ่งทำ <span>Digital Transformation</span>
                <br />
                หากยังไม่เคยประเมินความพร้อมองค์กร
              </h2>
            </div>
          </div>
        </div>

        <div className='AIReadiness__why-before'>
          <div className='AIReadiness__why-before__bg'>
            <Image
              alt='AI Readiness'
              src='/images/ai/readiness/digital.jpg'
              layout='fill'
              objectFit='cover'
            />
          </div>

          <div className='AIReadiness__why-before__content'>
            <div className='container'>
              <div className='AIReadiness__why-before__body'>
                <h2>
                  ทำไมถึงต้องทำ <span>AI Readiness Assessment</span> ก่อนเริ่มทำ
                  Digital Transformation?
                </h2>

                <p>
                  งานวิจัยจาก Kearney-EDBI Artificial Intelligence Study พบว่า
                  กว่า 83% ของธุรกิจในไทยที่นำ AI เข้ามาใช้งาน
                  ยังอยู่เพียงขั้นเริ่มต้นเท่านั้น มีเพียง 15%
                  ที่สามารถต่อยอดใช้ AI ในองค์กรได้อย่างสมบูรณ์
                </p>
                <p>
                  หลาย ๆ องค์กรเริ่มทำ Digital Transformation
                  โดยไม่ได้มีการวิเคราะห์ปัจจัยพื้นฐานที่เกี่ยวข้อง
                  หรือประเมินความพร้อมหลักในด้านต่าง ๆ ทั้ง Business และ
                  Technical capabilities มาก่อน ทำให้การ transform
                  เป็นไปได้ยากและมีความเสี่ยงที่จะล้มเหลว
                </p>
                <p>
                  ดังนั้นการประเมินความพร้อมขององค์กรด้วย AI Readiness
                  Assessment
                  นั้นเป็นรากฐานสำคัญที่จะช่วยประเมินศักยภาพขององค์กรพร้อมวิเคราะห์ปัจจัยต่าง
                  ๆ ที่ส่งผลกระทบเพื่อปูทาง ให้สามารถทำ Digital Transformation
                  ได้สำเร็จ
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='AIReadiness__assessment'>
          <div className='container'>
            <div className='AIReadiness__assessment__img'>
              <div className='AIReadiness__assessment__img__ratio'>
                <Image
                  src='/images/ai/readiness/assessment.jpg'
                  layout='fill'
                  objectFit='cover'
                  alt='AI Readiness Assessment'
                />
              </div>
            </div>

            <article>
              <p>
                <strong>AI Readiness Assessment</strong> คือ
                แบบประเมินความพร้อมขององค์กรในการพัฒนาและเปลี่ยนแปลงโครงสร้างพื้นฐานด้านข้อมูลและเทคโนโลยี
                รวมถึงการปรับตัวเชิงนโยบายเพื่อรองรับการนำเข้าและใช้งานองค์ความรู้ที่เกี่ยวข้องโดยตรงกับปัญญาประดิษฐ์
                (Artificial Intelligence: AI)
                เพื่อใช้เป็นแนวทางเบื้องต้นในการวางเป้าหมายและแผนงานสำหรับการเปลี่ยนผ่านเข้าสู่ยุคอุตสาหกรรม
                4.0
              </p>
              <p>
                <strong>Data Wow</strong> ให้บริการโดยเราจะจัดทีมงาน Data
                Experts
                เพื่อสอบถามข้อมูลและทำความเข้าใจบริบทภาพรวมขององค์กรในด้านต่าง ๆ
                หลังจากนั้นจะประเมินคะแนนด้วยแบบฟอร์มที่จัดทำขึ้นและชี้แจงสิ่งที่ควรแก้ไขตามลำดับ
              </p>
            </article>
          </div>

          <div className='AIReadiness__assessment__action'>
            <CTAButton label='about' />
          </div>
        </div>

        <div className='AIReadiness__why-readiness'>
          <div className='container'>
            <div className='AIReadiness__why-readiness__content'>
              <h2>
                ทำไมองค์กรของคุณถึงควรประเมินความพร้อม
                <br />
                ในการใช้งาน AI
              </h2>
              <p>
                เพื่อให้รู้ว่าองค์กรของคุณอยู่ในระดับไหนของ Industry Standard
                (2.0 - 4.0) และเทียบกับคู่แข่ง
                <br />
                ในอุตสาหกรรมเดียวกัน
                เรียนรู้เพิ่มเติมว่าทำไมถึงควรประเมินความพร้อมองค์กร{' '}
                <Link href='/blogs/assess-a-readiness-of-your-organization-by-AI-readiness-assessment'>
                  <a className='AIReadiness__why-readiness__link'>ที่นี่</a>
                </Link>
              </p>
            </div>

            <div className='AIReadiness__why-readiness__img'>
              <Image
                width={1124}
                height={777}
                alt='Why readiness'
                layout='responsive'
                src='/images/ai/readiness/graph.jpg'
              />
            </div>

            <ul className='AIReadiness__why-readiness__list'>
              {whyReadinessList.map((item) => {
                return (
                  <li
                    className='AIReadiness__why-readiness__item'
                    key={item.title2}
                  >
                    <div className='AIReadiness__why-readiness__icon'>
                      <Image
                        width={92}
                        height={92}
                        alt='Why readiness'
                        layout='fixed'
                        src={item.icon}
                      />
                    </div>

                    <h3>
                      {item.title1}
                      <br />
                      {item.title2}
                    </h3>
                    <p>{item.body}</p>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className='AIReadiness__why-us'>
          <div className='container'>
            <h2>
              ทำไมต้องประเมิน
              <br />
              AI Readiness Assessment กับเรา
            </h2>

            <p>
              Data Wow คือผู้ให้บริการ AI Software Development ชั้นนำในไทย
              เรามีทีมผู้เชี่ยวชาญที่มากประสบการณ์พร้อมให้ความช่วยเหลือและแนะนำการทำ
              AI Readiness Assessment
              สำหรับองค์กรของคุณโดยตรงเพื่อช่วยพัฒนาให้ธุรกิจของคุณเติบโตไปด้วยกัน
              เริ่มเตรียมความพร้อมให้ธุรกิจของคุณตั้งแต่วันนี้
            </p>
          </div>

          <CTAButton label='why' />
        </div>

        <div className='AIReadiness__what'>
          <div className='container'>
            <div>
              <h2 className='AIReadiness__what__title AIReadiness__what__title--sm'>
                สิ่งที่คุณจะได้รับ
              </h2>
              <Image
                width={498}
                height={470}
                alt='AI Readiness Sample'
                layout='responsive'
                src='/images/ai/readiness/sample.jpg'
                className='AIReadiness__what__img'
              />
            </div>

            <div className='AIReadiness__what__content'>
              <h2 className='AIReadiness__what__title AIReadiness__what__title--md'>
                สิ่งที่คุณจะได้รับ
              </h2>
              <h3>Extensive Report</h3>
              <p>
                คุณจะได้รับ report
                ที่ประกอบด้วยคะแนนประเมินในแต่ละด้านพร้อมคำอธิบายถึงสิ่งที่ควรปรับปรุงและแนวทางการจัดลำดับความสำคัญสำหรับขั้นตอนการปฏิบัติ
              </p>

              <h3>Free Consultation Session(s)</h3>
              <p>
                คำปรึกษาเพิ่มเติมจาก Data Experts
                ที่จะช่วยในเรื่องของการจัดการและบริหารข้อมูลภายในองค์กรรวมถึงต่อยอดสู่การนำเทคโนโลยี
                AI ไปใช้ในการทำโครงการต่าง ๆ
              </p>
            </div>
          </div>
        </div>

        <ContactForm />
      </div>
    </React.Fragment>
  )
}

AIReadiness.getLayout = function getLayout(page: React.ReactElement) {
  return <MainLayout>{page}</MainLayout>
}
